import { useEffect } from 'react';
import { foldLayoutAllowed } from './device';
import { WIDE_LAYOUT } from './responsive';

// Folding and unfolding (2026-10-25: iPhone Duo, Pixel Fold, Galaxy Z Fold). The screen changes width under the open
// page — unfolded, the area workspace is two columns; folded, one — and the document kept its pixel offset, so the
// field being typed in, or the block that was in view, ended up far above or below the screen. When a wide layout is
// involved on either side of a width change, the focused field is brought back into view, otherwise the block that
// was at the top of the screen goes back to the top. A phone is never wide (narrower than WIDE_LAYOUT.minWidth held
// upright, shorter than WIDE_LAYOUT.minHeight turned sideways), so on a phone this never acts.

export interface ViewportSize {
  width: number;
  height: number;
}

export function isWideViewport(size: ViewportSize): boolean {
  return size.width >= WIDE_LAYOUT.minWidth && size.height >= WIDE_LAYOUT.minHeight;
}

// Only a width change with a wide layout before or after it re-lays the page out (the keyboard changes the height only).
export function foldReanchors(before: ViewportSize, after: ViewportSize): boolean {
  return before.width !== after.width && (isWideViewport(before) || isWideViewport(after));
}

interface Anchor {
  element: Element;
  top: number;
}

const ANCHOR_BLOCK = '.card, .item-card, .area-section, .list-row, .field, [data-testid]';

function viewportSize(): ViewportSize {
  return { width: window.innerWidth, height: window.innerHeight };
}

// An open sheet pins the page (src/ui/scrollLock.ts) and restores its own offset when it closes.
function pagePinned(): boolean {
  return document.body.style.position === 'fixed';
}

function focusedField(): HTMLElement | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !active.closest('.app-frame')) return null;
  return active.matches('input, textarea, select, [contenteditable="true"]') ? active : null;
}

// The block just under the sticky top bar: near the content's leading edge, or — where that column has already ended,
// as the area workspace's left column does under a long checklist — near its trailing edge.
function topBlock(): Anchor | null {
  const frame = document.querySelector('.app-frame');
  if (!frame || typeof document.elementFromPoint !== 'function') return null;
  const box = frame.getBoundingClientRect();
  const bars = Array.from(document.querySelectorAll('.topbar, .shell-topbar'), (bar) => bar.getBoundingClientRect().bottom);
  const y = Math.max(0, ...bars) + 8;
  for (const x of [box.left + 40, box.right - 40]) {
    const hit = document.elementFromPoint(x, y);
    const element = hit && frame.contains(hit) ? hit.closest(ANCHOR_BLOCK) : null;
    // a whole screen is no anchor: its top is wherever the page is scrolled to
    if (element && !element.matches('.screen')) return { element, top: element.getBoundingClientRect().top };
  }
  return null;
}

export function useFoldScrollAnchor(): void {
  useEffect(() => {
    // An iPad and the web version keep their behaviour exactly as it was (src/app/device.ts).
    if (!foldLayoutAllowed()) return undefined;
    let last = viewportSize();
    let anchor: Anchor | null = null;
    let timer = 0;
    // scrolls this hook makes itself do not choose a new anchor, so folding and unfolding again returns to the same block
    let ownScrollUntil = 0;
    // The outer screen of a foldable is phone-sized, so the block in view is noted on every screen — once scrolling
    // has settled, never while it runs. Nothing is moved here.
    const record = () => {
      timer = 0;
      if (!pagePinned() && Date.now() >= ownScrollUntil) anchor = topBlock();
    };
    const remember = () => {
      if (timer !== 0) window.clearTimeout(timer);
      timer = window.setTimeout(record, 150);
    };
    const onResize = () => {
      const now = viewportSize();
      const before = last;
      last = now;
      if (!foldReanchors(before, now) || pagePinned()) return;
      const field = focusedField();
      const held = anchor;
      // once React has re-rendered for the new tier (the shell bar comes and goes at 1024); a timer, not an
      // animation frame, so it also runs while the WebView is not painting yet
      window.setTimeout(() => {
        ownScrollUntil = Date.now() + 400;
        if (field && field.isConnected) field.scrollIntoView({ block: 'center' });
        else if (held && held.element.isConnected) window.scrollBy(0, held.element.getBoundingClientRect().top - held.top);
        anchor = held && held.element.isConnected ? { element: held.element, top: held.top } : anchor;
      }, 50);
    };
    remember();
    window.addEventListener('scroll', remember, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', remember);
      window.removeEventListener('resize', onResize);
      if (timer !== 0) window.clearTimeout(timer);
    };
  }, []);
}
